import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Meta from '../components/Meta';
import './ProjectDetails.css';

// Images
import atbookImg from '../assets/projects/atbook-project1.png';
import atbookImg2 from '../assets/projects/atbook-project2.png';
import atbookImg3 from '../assets/projects/atbook-project3.png';
import bienebImg from '../assets/projects/bieneb-project.png';
import altajImg from '../assets/projects/altaj-project.png';

const projectImages = {
    atbook: [atbookImg, atbookImg2, atbookImg3],
    bieneb: [bienebImg],
    altaj: [altajImg]
};

function ProjectDetails() {
    const { t } = useTranslation();
    const { id } = useParams();
    const images = projectImages[id];

    // Fetch project content from JSON (returns object or string key if missing)
    const projectContent = t(`project_details.${id}`, { returnObjects: true });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    if (!images || typeof projectContent === 'string') {
        return (
            <div className="project-not-found">
                <h2>{t('project_details.not_found', 'Projekt nicht gefunden')}</h2>
                <Link to="/projekte" className="back-link">{t('project_details.back_link')}</Link>
            </div>
        );
    }

    return (
        <div className="project-details-page">
            <Meta
                title={`${projectContent.title} | Vorpoint`}
                description={projectContent.subtitle}
            />

            <section className="project-hero">
                <motion.div
                    className="project-hero-content"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="project-badges">
                        <span className="category-badge">{projectContent.category}</span>
                    </div>
                    <h1>{projectContent.title}</h1>
                    <p className="subtitle">{projectContent.subtitle}</p>
                </motion.div>
            </section>

            <div className="project-details-container">
                <motion.div
                    className="project-main-image"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <img src={images[0]} alt={projectContent.title} />
                </motion.div>

                <div className="project-content-wrapper">
                    {/* Tags - require update in JSON to exist */}
                    {projectContent.tags && (
                        <div className="project-tags-list">
                            {projectContent.tags.map((tag, i) => (
                                <span key={i} className="detail-tag">{tag}</span>
                            ))}
                        </div>
                    )}

                    <div className="project-body-content">
                        {projectContent.intro_title && <h2>{projectContent.intro_title}</h2>}
                        {projectContent.intro_text && <p>{projectContent.intro_text}</p>}

                        {/* BieneB Specific: Problem/Solution if available (or part of content flow) */}
                        {/* Since structure varies, we adapt based on keys */}

                        {/* Features Grid (Atbook / Altaj) */}
                        {projectContent.features && (
                            <div className="features-grid-details">
                                {projectContent.features.map((feature, i) => (
                                    <div key={i} className="feature-card-detail">
                                        <h3>{feature.title}</h3>
                                        <p>{feature.text}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Modules List (BieneB) */}
                        {projectContent.modules && (
                            <div className="modules-list">
                                {projectContent.modules.map((module, i) => (
                                    <div key={i} className="module-item">
                                        <h4>{module.title}</h4>
                                        <p>{module.text}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Advantages List (BieneB) */}
                        {projectContent.advantages && (
                            <div className="advantages-section">
                                <h3>Vorteile / Advantages</h3>
                                <ul className="detail-list">
                                    {projectContent.advantages.map((adv, i) => (
                                        <li key={i}>{adv}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Contact Box (Altaj) */}
                        {projectContent.contact_box && (
                            <div className="contact-box-detail">
                                <h4>{projectContent.contact_box.title}</h4>
                                <p>{projectContent.contact_box.phone}</p>
                                <p>{projectContent.contact_box.slogan}</p>
                            </div>
                        )}
                    </div>

                    {/* Extra images gallery */}
                    {images.length > 1 && (
                        <div className="project-gallery">
                            {images.slice(1).map((img, i) => (
                                <img key={i} src={img} alt={`${projectContent.title} view ${i + 2}`} />
                            ))}
                        </div>
                    )}

                    <div className="project-navigation">
                        <Link to="/projekte" className="back-link">
                            ← {t('project_details.back_link')}
                        </Link>
                        <Link to="/kontakt" className="cta-link">
                            {t('project_details.cta_button')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProjectDetails;
